<template>
    <Disclosure as="nav" class="fixed top-0 left-0 right-0 z-50 border-b border-gray-200 bg-white sm:bg-white/80 sm:backdrop-filter sm:backdrop-blur-lg shadow-xs" v-slot="{ open }">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="flex h-16 justify-between">
                <div class="flex">
                    <router-link to="/" class="flex shrink-0 items-center">
                        <img class="block h-8 w-auto lg:hidden" src="@/assets/logo.svg" alt="Gallera" />
                        <img class="hidden h-8 w-auto lg:block" src="@/assets/logo.svg" alt="Gallera" />
                    </router-link>
                    <div class="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                        <template
                            v-for="item in navigation"
                            :key="item.name">
                            <router-link
                                :to="item.href"
                                v-if="item.routeName !== false"
                                :class="[(item.routeName !== false && isCurrentRoute(item.routeName))
                                    ? 'border-royal-blue-600 text-midnight-950'
                                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                    'relative inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium'
                                ]"
                                :aria-current="(item.routeName !== false && isCurrentRoute(item.routeName)) ? 'page' : undefined">
                                <span class="hidden lg:block">{{ item.name }}</span>
                                <span class="block lg:hidden">{{ item.short_name }}</span>
                            </router-link>
                            <a :href="item.href"
                                target="_blank"
                                v-if="!item.routeName"
                                :class="[
                                    'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                    'hidden lg:inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium'
                                ]">
                                {{ item.name }}
                            </a>
                        </template>
                    </div>
                </div>

                <div class="hidden sm:ml-6 sm:flex sm:items-center">
                    <router-link to="/subscription" class="hidden md:block shrink-0 mr-3 group hover:opacity-90">
                        <span class="flex flex-col gap-1">
                            <span class="text-gray-500 text-sm inline-flex items-center gap-x-3">
                                <span><strong>{{ storageUsed }}</strong> / {{ storageLimit }}</span>
                                <span class="bg-royal-blue-500 text-white rounded-full group-hover:bg-royal-blue-600 p-0.5 text-xs font-semibold">
                                    <PlusIcon class="size-3" aria-hidden="true" />
                                </span>
                            </span>
                            <span class="w-full bg-gray-200 h-1.5 rounded outline outline-1 outline-gray-100">
                                <span
                                    :class="{
                                      'bg-yellow-500': storageUsedPercent >= 90 && storageUsedPercent < 100,
                                      'bg-red-500': storageUsedPercent >= 100,
                                      'bg-royal-blue-500': storageUsedPercent < 90
                                    }"
                                    :style="{ width: `${Math.min(storageUsedPercent, 100)}%` }"
                                    class="h-full rounded block"
                                ></span>
                            </span>
                        </span>
                    </router-link>

                    <div class="shrink-0">
                        <a href="#"
                            @click.prevent="openCreateGallery"
                            class="relative inline-flex items-center gap-x-1.5 mr-3 rounded-md bg-royal-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-royal-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-500">
                            <PlusIcon class="-ml-0.5 size-5" aria-hidden="true" />
                            {{ $t('navigation.create_gallery') }}
                        </a>
                    </div>

<!--                    <button type="button" class="relative mx-2 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden">-->
<!--                        <span class="absolute -inset-1.5" />-->
<!--                        <span class="sr-only">Сповіщення</span>-->
<!--                        <BellIcon class="size-6" aria-hidden="true" />-->
<!--                    </button>-->

                    <div class="relative size-10">
                        <svg class="size-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                            <!-- Background Circle -->
                            <circle cx="18" cy="18" r="16" fill="none" class="stroke-current text-gray-200" stroke-width="2"></circle>
                            <!-- Progress Circle -->
                            <circle cx="18" cy="18" r="16" fill="none" class="stroke-current text-royal-blue-500" stroke-width="2" stroke-dasharray="100" :stroke-dashoffset="100 - Math.min(storageUsedPercent, 100)" stroke-linecap="round"></circle>
                        </svg>

                        <Menu as="div" class="absolute top-1/2 left-1/2 transform w-full p-1 -translate-y-1/2 -translate-x-1/2">
                            <div v-if="isLoggedIn && user">
                                <MenuButton class="relative flex rounded-full bg-white text-sm focus:outline-hidden">
                                    <span class="absolute -inset-1.5" />
                                    <span class="sr-only">{{ $t('navigation.user_menu') }}</span>
                                    <img class="size-8 rounded-full" :src="user.avatar_url" alt="" />
                                </MenuButton>
                            </div>
                            <transition enter-active-class="transition ease-out duration-200" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 ring-1 shadow-lg ring-black/5 focus:outline-hidden">
                                    <MenuItem class="border-b border-royal-blue-600">
                                        <router-link to="/subscription" class="block px-4 py-2 text-sm text-royal-blue-600">
                                            <span><strong>{{ storageUsed }}</strong> {{ $t('navigation.storage_of') }} {{ storageLimit }}</span>
                                        </router-link>
                                    </MenuItem>
                                    <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                                        <router-link :to="item.href" :class="[active ? 'bg-gray-100 outline-hidden' : '', 'block px-4 py-2 text-sm text-gray-700']">{{ item.name }}</router-link>
                                    </MenuItem>
                                    <MenuItem v-slot="{ active }">
                                        <a href="#" @click.prevent="doLogout" :class="[active ? 'bg-gray-100 outline-hidden' : '', 'block w-full px-4 py-2 text-left text-sm text-gray-700 cursor-pointer']">
                                            {{ $t('navigation.logout') }}
                                        </a>
                                    </MenuItem>
                                </MenuItems>
                            </transition>
                        </Menu>
                    </div>
                </div>
                <div class="-mr-2 flex items-center gap-2 sm:hidden">
                    <button
                        @click.prevent="openCreateGallery"
                        class="relative inline-flex items-center gap-x-1 justify-center rounded-md bg-white px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden shadow-xs">
                        <PlusIcon class="size-4" aria-hidden="true" />
                        <span>{{ $t('navigation.create_gallery') }}</span>
                    </button>
                    <div class="h-6 w-px bg-gray-300"></div>
                    <DisclosureButton class="relative inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden">
                        <span class="absolute -inset-0.5" />
                        <span class="sr-only">{{ $t('navigation.open_main_menu') }}</span>
                        <Bars3Icon v-if="!open" class="block size-6" aria-hidden="true" />
                        <XMarkIcon v-else class="block size-6" aria-hidden="true" />
                    </DisclosureButton>
                </div>
            </div>
        </div>

        <DisclosurePanel class="sm:hidden" v-if="isLoggedIn && user">
            <div class="space-y-1 pt-2 pb-3">
                <a href="#" @click.prevent="openCreateGallery" class="border-transparent text-royal-blue-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 block border-l-4 py-2 pr-4 pl-3 text-base font-medium">{{ $t('navigation.create_gallery') }}</a>
                <template v-for="item in navigation" :key="item.name">
                    <router-link v-if="item.routeName" :to="item.href" v-slot="{ href, navigate }">
                        <DisclosureButton as="a" :href="href" @click="navigate" :class="[(item.routeName !== false && isCurrentRoute(item.routeName)) ? 'border-royal-blue-500 bg-royal-blue-50 text-royal-blue-500' : 'border-transparent text-midnight-950 hover:border-gray-300 hover:bg-gray-50 hover:text-midnight-800', 'block border-l-4 py-2 pr-4 pl-3 text-base font-medium']" :aria-current="(item.routeName !== false && isCurrentRoute(item.routeName)) ? 'page' : undefined">{{ item.name }}</DisclosureButton>
                    </router-link>
                    <a :href="item.href" target="_blank" :class="['border-transparent text-midnight-950 hover:border-gray-300 hover:bg-gray-50 hover:text-midnight-800', 'block border-l-4 py-2 pr-4 pl-3 text-base font-medium']" v-if="!item.routeName">
                        {{ item.name }}
                    </a>
                </template>
            </div>
            <div class="border-t border-gray-200 pt-4 pb-3">
                <div class="flex items-center px-4">
                    <div class="shrink-0">
                        <img class="size-10 rounded-full" :src="user.avatar_url" alt="" />
                    </div>
                    <div class="ml-3">
                        <div class="text-base font-medium text-gray-800">{{ user.name }}</div>
<!--                        <div class="text-sm font-medium text-gray-500">email here</div>-->
                    </div>
<!--                    <button type="button" class="relative ml-auto shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden">-->
<!--                        <span class="absolute -inset-1.5" />-->
<!--                        <span class="sr-only">Сповіщення</span>-->
<!--                        <BellIcon class="size-6" aria-hidden="true" />-->
<!--                    </button>-->
                </div>
                <div class="mt-3 px-4">
                    <span class="flex flex-col gap-1">
                        <span class="text-gray-500 text-sm inline-flex items-center gap-x-3">
                            <span><strong>{{ storageUsed }}</strong> / {{ storageLimit }}</span>
                            <span class="bg-royal-blue-500 text-white rounded-full group-hover:bg-royal-blue-600 p-0.5 text-xs font-semibold">
                                <PlusIcon class="size-3" aria-hidden="true" />
                            </span>
                        </span>
                        <span class="w-full bg-gray-200 h-1.5 rounded outline outline-1 outline-gray-100">
                            <span
                                :class="{
                                  'bg-yellow-500': storageUsedPercent >= 90 && storageUsedPercent < 100,
                                  'bg-red-500': storageUsedPercent >= 100,
                                  'bg-royal-blue-500': storageUsedPercent < 90
                                }"
                                :style="{ width: `${Math.min(storageUsedPercent, 100)}%` }"
                                class="h-full rounded"
                            ></span>
                        </span>
                    </span>
                </div>
                <div class="mt-3 space-y-1">
                    <router-link v-for="item in userNavigation" :key="item.name" :to="item.href" v-slot="{ href, navigate }">
                        <DisclosureButton as="a" :href="href" @click="navigate" class="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800">{{ item.name }}</DisclosureButton>
                    </router-link>
                    <DisclosureButton as="a" href="#" @click.prevent="doLogout" class="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                        {{ $t('navigation.logout') }}
                    </DisclosureButton>
                </div>
            </div>
        </DisclosurePanel>
    </Disclosure>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {mapActions, mapGetters} from "vuex";

import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { Bars3Icon, XMarkIcon, PlusIcon } from '@heroicons/vue/24/outline'
import {formatStorage} from "@/_helpers/formatters";

export default defineComponent({
    name: 'NavigationComponent',

    components: {
        Disclosure,
        DisclosureButton,
        DisclosurePanel,
        Menu, MenuButton, MenuItem, MenuItems,
        Bars3Icon, XMarkIcon, PlusIcon
    },

    data: function () {
        return {
            navigation: [
                { name: this.$t('navigation.my_galleries'), short_name: this.$t('navigation.my_galleries_short'), href: '/', routeName: "home" },
                { name: this.$t('navigation.my_profile'), short_name: this.$t('navigation.my_profile_short'), href: '/profile', routeName: "user.profile" },
                { name: this.$t('navigation.help'), short_name: this.$t('navigation.help_short'), href: this.$i18n.locale === 'en' ? 'https://help.gallera.io/?lang=en' : 'https://help.gallera.io/', routeName: false },
            ],

            userNavigation: [
                { name: this.$t('navigation.subscription'), href: '/subscription' },
                { name: this.$t('navigation.settings'), href: '/settings' },
            ]
        };
    },

    mounted() {
        //
    },

    computed: {
        ...mapGetters({
            isLoggedIn: 'user/isLoggedIn',
            user: 'user/user'
        }),

        storageUsed(): string {
            return formatStorage(this.user ? this.user.storage.used : 0, this.$t);
        },

        storageLimit(): string {
            return formatStorage(this.user ? this.user.storage.limit : 0, this.$t);
        },

        storageUsedPercent(): number {
            if (!this.user) {
                return 0;
            }

            return (this.user.storage.used / this.user.storage.limit) * 100;
        }
    },

    methods: {
        ...mapActions({
            logout: "user/logout",
        }),

        publicUrl(url: string): string {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },

        isCurrentRoute(route: string): boolean {
            return this.$route.name === route;
        },

        doLogout() {
            this.logout()
                .then(() => {
                    this.$router.push('/login');

                    location.reload();
                })
        },

        openCreateGallery() {
            this.$emit('open-create-gallery');
        }
    }
});
</script>

<style lang="scss" scoped>

</style>